<template>
  <div class="tw-flex tw-flex-col tw-justify-center tw-h-full tw-text-center">
    <template v-if="resourceLimitModalType === 'resoucesRestored'">
      <div
        class="tw-flex tw-flex-col tw-justify-center tw-items-center tw-my-4"
      >
        <v-img
          src="@/assets/calendar.svg"
          width="110"
          height="110"
          class="tw-relative tw-overflow-visible"
        />
      </div>
      <div class="tw-gap-2 tw-flex tw-flex-col tw-mb-4 tw-text-slate-600">
        <p class="tw-text-xl tw-font-semibold">
          {{ $t('resourceUseLimitInfo.resourcesRestored.title') }}
        </p>

        <p class="tw-text-sm">
          {{ $t('resourceUseLimitInfo.resourcesRestored.subtitle') }}
        </p>
      </div>

      <div class="tw-px-5 tw-mb-4">
        <v-card
          variant="outlined"
          color="primary"
          class="tw-rounded-xl tw-text-sm tw-py-6 tw-px-8 tw-bg-primary-500/5"
        >
          <v-list
            :density="false"
            class="tw-text-left tw-bg-transparent tw-space-y-3 tw-my-0"
          >
            <v-list-item class="tw-p-0 tw-grid-cols-[24px_1fr_auto]">
              <template #prepend>
                <v-icon
                  icon="mdi-check"
                  class="tw-text-primary-500 tw-text-xl"
                />
              </template>
              <span
                v-html="
                  $t(
                    'resourceUseLimitInfo.resourcesRestored.benefits.consultationsAndTime',
                    {
                      consultationsLimit: consultationsLimit,
                      hoursLimit: secondsToHours(recordingLimitSeconds),
                    },
                  )
                "
              ></span>
            </v-list-item>
            <v-list-item class="tw-p-0 tw-grid-cols-[24px_1fr_auto]">
              <template #prepend>
                <v-icon
                  icon="mdi-check"
                  class="tw-text-primary-500 tw-text-xl"
                />
              </template>
              <span
                v-html="
                  $t(
                    'resourceUseLimitInfo.resourcesRestored.benefits.customModel',
                    {
                      maxModels: MAX_FREE_CUSTOM_PROMPTS_TOTAL,
                    },
                  )
                "
              ></span>
            </v-list-item>
            <v-list-item class="tw-p-0 tw-grid-cols-[24px_1fr_auto]">
              <template #prepend>
                <v-icon
                  icon="mdi-check"
                  class="tw-text-primary-500 tw-text-xl"
                />
              </template>
              <span
                v-html="
                  $t('resourceUseLimitInfo.resourcesRestored.benefits.tabs', {
                    tabsLimit:
                      userPlanLimits?.outputTabsPerConsultation ??
                      CUSTOM_PROMPTS_LIMITS.tabs,
                  })
                "
              ></span>
            </v-list-item>
          </v-list>
        </v-card>
      </div>
    </template>

    <template
      v-else-if="['customPrompts', 'maxTabsLimit'].includes(resourceLimitModalType)"
    >
      <div
        class="tw-flex tw-flex-col tw-justify-center tw-items-center tw-my-4"
      >
        <div class="tw-bg-primary-500/5 tw-rounded-full tw-p-6 tw-mb-4">
          <v-img
            src="@/assets/lock.svg"
            width="70"
            height="80"
            class="tw-relative tw-overflow-visible"
          >
            <v-img
              src="@/assets/daai-sad-blue.svg"
              width="87"
              height="24"
              class="tw-absolute tw-left-0 tw-translate-y-1/4 -tw-translate-x-[110%] tw-w-[150px]"
            />
          </v-img>
        </div>

        <div class="tw-gap-3 tw-flex tw-flex-col tw-mb-4">
          <p
            v-if="resourceLimitModalType === 'customPrompts'"
            class="tw-text-xl tw-text-slate-600"
            v-html="
              $t('resourceUseLimitInfo.customPrompts.title', {
                category: $t(
                  `resourceUseLimitInfo.customPrompts.category.${
                    isAllCustomPromptsLimitReached ? 'all' : 'specific'
                  }`,
                ),
              })
            "
          ></p>

          <p
            v-else-if="resourceLimitModalType === 'maxTabsLimit'"
            class="tw-text-xl tw-text-slate-600"
            v-html="$t('resourceUseLimitInfo.maxTabsLimit.title')"
          ></p>

          <p
            v-if="resourceLimitModalType === 'customPrompts'"
            class="tw-text-sm tw-font-light tw-text-slate-600"
          >
            {{ $t('resourceUseLimitInfo.customPrompts.description') }}
          </p>

          <p
            v-else-if="resourceLimitModalType === 'maxTabsLimit'"
            class="tw-text-sm tw-font-light tw-text-slate-600"
          >
            {{ $t('resourceUseLimitInfo.maxTabsLimit.description') }}
          </p>
        </div>
      </div>
    </template>

    <template
      v-else-if="
        resourceLimitModalType === 'consultations' &&
        !exceededConsultationsLimit
      "
    >
      <div
        class="tw-flex tw-flex-col tw-justify-center tw-items-center tw-my-8"
      >
        <v-img
          src="@/assets/resource-limit-laptop.svg"
          width="180"
          height="auto"
          alt="ResourceUseLimitInfo"
          class="tw-relative tw-mb-3 tw-overflow-visible"
        >
          <div
            class="tw-absolute -tw-top-0 tw-right-0 -tw-translate-x-1/4 -tw-translate-y-2/3 tw-size-12 tw-bg-primary-500 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-2xl tw-font-semibold tw-text-white tw-z-10"
          >
            {{ totalConsultationsInPeriod }}
          </div>
        </v-img>
        <p class="tw-text-sm tw-text-slate-500">
          <v-icon
            icon="mdi-stethoscope"
            class="tw-text-xl tw-align-middle tw-mr-1"
          />
          {{ $t('resourceUseLimitInfo.consultations.label') }}
          <span class="tw-text-slate-700">
            {{ totalConsultationsInPeriod }} / {{ consultationsLimit }}
          </span>
        </p>
      </div>

      <div class="tw-gap-3 tw-flex tw-flex-col tw-mb-8">
        <p class="tw-text-base tw-text-slate-600 tw-font-semibold">
          {{
            $t('resourceUseLimitInfo.consultations.title', {
              count: totalConsultationsInPeriod,
            })
          }}
        </p>
        <p>
          {{
            $t('resourceUseLimitInfo.consultations.remaining', {
              remaining: consultationsLimit - totalConsultationsInPeriod,
            })
          }}
        </p>
      </div>
    </template>

    <template
      v-else-if="
        resourceLimitModalType === 'recordingTime' &&
        !exceededRecordingTimeLimit
      "
    >
      <div
        class="tw-flex tw-flex-col tw-justify-center tw-items-center tw-my-8"
      >
        <v-img
          src="@/assets/resource-time-limit-laptopsvg.svg"
          width="180"
          height="auto"
          alt="ResourceUseLimitInfo"
          class="tw-relative tw-mb-3 tw-overflow-visible"
        >
          <div
            class="tw-absolute -tw-top-0 tw-right-0 -tw-translate-x-1/4 -tw-translate-y-2/3 tw-size-12 tw-bg-primary-500 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-2xl tw-font-semibold tw-text-white tw-z-10"
          >
            !!
          </div>
        </v-img>
        <p class="tw-text-sm tw-text-slate-500">
          <v-icon
            icon="mdi-clock-outline"
            class="tw-text-xl tw-align-middle tw-mr-1"
          />
          {{ $t('resourceUseLimitInfo.recordingTime.label') }}
          <span class="tw-text-slate-700">
            {{ formatSecondsToTime(totalRecordingTimeInPeriod) }} /
            {{ formatSecondsToTime(recordingLimitSeconds) }}
          </span>
        </p>
      </div>

      <div class="tw-gap-3 tw-flex tw-flex-col tw-mb-8">
        <p class="tw-text-base tw-text-slate-600 tw-font-semibold">
          {{
            $t('resourceUseLimitInfo.recordingTime.title', {
              timeUsed: formatSecondsToTime(totalRecordingTimeInPeriod),
            })
          }}
        </p>
        <p>
          {{
            $t('resourceUseLimitInfo.recordingTime.remaining', {
              timeRemaining: formatSecondsToTime(
                recordingLimitSeconds - totalRecordingTimeInPeriod,
              ),
            })
          }}
        </p>
      </div>
    </template>

    <ExeceededResourcesInfoCard
      v-else-if="isResourceUseLimitReached"
      :type="resourceLimitModalType"
    />

    <v-btn
      variant="outlined"
      rounded
      size="large"
      class="tw-text-sm tw-font-semibold tw-text-primary-500 tw-opacity-100 tw-self-center tw-border-0 hover:tw-border"
      @click="handleContinueFreePlan"
    >
      {{
        isResourceUseLimitReached ||
        ['customPrompts', 'maxTabsLimit'].includes(resourceLimitModalType)
          ? $t('resourceUseLimitInfo.buttons.continueWithLimits')
          : $t('resourceUseLimitInfo.buttons.continueFreePlan')
      }}
    </v-btn>
  </div>
</template>

<script setup>
import { secondsToHours } from 'date-fns'
import { useUserMetricsStore } from '@/stores/userMetrics'
import { useUserStore } from '@/stores/user'
import { useModalsStore } from '@/stores/modals'
import {
  RECORDING_TIME_LIMIT,
  CONSULTATIONS_LIMIT,
  CUSTOM_PROMPTS_LIMITS,
  MAX_FREE_CUSTOM_PROMPTS_TOTAL,
} from '@/constants/resourceLimits'
import { formatSecondsToTime } from '@/utils/masks'

const userMetricsStore = useUserMetricsStore()
const userStore = useUserStore()
const modalsStore = useModalsStore()
const { limits: userPlanLimits } = storeToRefs(userStore)
const {
  resourceLimitModalType,
  totalConsultationsInPeriod,
  totalRecordingTimeInPeriod,
  exceededConsultationsLimit,
  exceededRecordingTimeLimit,
  isResourceUseLimitReached,
  isAllCustomPromptsLimitReached,
} = storeToRefs(userMetricsStore)
const { showResourceUseLimitModal } = storeToRefs(modalsStore)

const consultationsLimit = computed(
  () => userPlanLimits.value?.consultationsPerMonth ?? CONSULTATIONS_LIMIT,
)
const recordingLimitSeconds = computed(
  () =>
    userPlanLimits.value?.recordingSecondsPerMonth ?? RECORDING_TIME_LIMIT,
)

const emit = defineEmits(['update:modelValue'])

const handleContinueFreePlan = () => {
  showResourceUseLimitModal.value = false
}
</script>

<style lang="scss" scoped></style>
